import type { History } from 'history';
import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';

import ExpectedNotifyResultModal from 'af-root/scenes/Company/ScheduleBoard/Shared/ExpectedNotifyResultModal';
import NotifyAllParticipantsModal from 'af-root/scenes/Company/ScheduleBoard/Shared/NotifyAllParticipantsModal';
import WorkOrderCopyModal from 'af-root/scenes/Company/WorkOrders/Table/OrderCopyForm';

import type { W_Notification_FindExpectedNotifyResult_VM } from 'ab-api/web/notification/findExpectedNotifyResult';
import type { W_WorkOrder_WorkOrderNotifyLabor_VM_WorkOrder } from 'ab-api/web/workOrder/findNotificationsForEmployees';

import type { DailyTipViewModel } from 'ab-viewModels/dailyTip.viewModel';
import type { EmailAndSmsStatusViewModelMap, EmailAndSmsStatusViewModel } from 'ab-viewModels/emailAndSmsStatus.viewModel';
import type { NotificationStatusByEmployee, NotificationStatusByTemporaryEmployee } from 'ab-viewModels/notification.viewModel';
import type ScheduleBoardLaborStatistics from 'ab-viewModels/scheduleBoardLaborStatistics.viewModel';

import type WorkOrderCopyRM from 'ab-requestModels/workOrder/copyWorkOrders.requestModel';

import type ScheduleBoardWorkOrderViewModel from 'ab-socketModels/viewModels/scheduleBoard/scheduleBoardWorkOrder.viewModel';

import { filterMap } from '@acceligentllc/shared/utils/array';
import { isAllowed } from '@acceligentllc/shared/utils/auth';
import * as TimeUtils from '@acceligentllc/shared/utils/time';

import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';
import { WorkOrderStatus } from '@acceligentllc/shared/enums/workOrderStatus';
import { WorkRequestStatus } from '@acceligentllc/shared/enums/workRequestStatus';

import type { RootState } from 'af-reducers';

import * as ScheduleBoardUtil from 'af-utils/scheduleBoard.util';
import { convertWorkDays } from 'af-utils/workDay.util';
import * as WorkOrderUtils from 'af-utils/workOrder.util';

import PagePermissions from 'ab-enums/pagePermissions.enum';

import CLIENT from 'af-constants/routes/client';
import { SCREEN_BREAKPOINT_M, SCREEN_BREAKPOINT_L } from 'af-constants/values';

import { Button } from '@acceligentllc/storybook';

import Checkbox from 'af-components/Controls/Checkbox';
import LaborStatisticsPerLocation from 'af-components/LaborStatistics/LaborStatisticsPerLocation';
import TotalLaborStatistics from 'af-components/LaborStatistics/TotalLaborStatistics';

import type { BaseWorkOrderModel } from 'af-models/scheduleBoard/scheduleBoard.models';

import * as DailyTipActions from 'af-actions/dailyTip';
import * as NotifyActions from 'af-actions/notify';
import * as ScheduleBoardActions from 'af-actions/scheduleBoard';
import * as WorkOrderActions from 'af-actions/workOrder';

import BigButton from './BigButton';
import SmallButton from './SmallButton';
import type { EmployeeAssignments, TemporaryEmployeeAssignments } from './types';

interface OwnProps {
	history: History;
	orgAlias: string;
	dueDate: string;
	companyName: string;
	isDragAndDropDisabled: boolean;
	hasPermissionsToEditScheduleBoard: boolean;
}

type Props = OwnProps & ConnectedProps<typeof connector>;

interface State {
	copyDayModalVisible: boolean;
	buttonDisplayLevel: number;
	notifyAllModalVisible: boolean;
	notificationStatusByEmployee: NotificationStatusByEmployee;
	notificationStatusByTemporaryEmployee: NotificationStatusByTemporaryEmployee;
	workOrderNotifications: W_WorkOrder_WorkOrderNotifyLabor_VM_WorkOrder[];
	showExpectedNotificationResultModal: boolean;
	assignmentsThatMightNotBeNotified: Nullable<W_Notification_FindExpectedNotifyResult_VM>;
}

class ToolbarInfo extends React.PureComponent<Props, State> {
	static defaultProps: Partial<Props> = {
		workDays: [],
		laborStatistics: {
			laborStatisticsPerLocation: undefined,
			totalLaborStatistics: undefined,
		},
		employees: {},
		dailyTip: {} as DailyTipViewModel,
	};

	state: State = {
		copyDayModalVisible: false,
		buttonDisplayLevel: 0,
		notifyAllModalVisible: false,
		notificationStatusByEmployee: {
			...(this.props.assignedCanceledNotificationStatuses ?? {}),
			...(this.props.assignedPublishedNotificationStatuses ?? {}),
		},
		notificationStatusByTemporaryEmployee: {
			...(this.props.assignedPublishedTempLaborNotificationStatuses ?? {}),
			...(this.props.assignedCanceledTempLaborNotificationStatuses ?? {}),
		},
		workOrderNotifications: [],
		showExpectedNotificationResultModal: false,
		assignmentsThatMightNotBeNotified: null,
	};

	componentDidMount() {
		this.updateWindowDimensions();
		window.addEventListener('resize', this.updateWindowDimensions);
	}

	componentDidUpdate(prevProps: Props) {
		this.updateWindowDimensions();

		const {
			assignedCanceledNotificationStatuses,
			assignedPublishedNotificationStatuses,
			assignedPublishedTempLaborNotificationStatuses,
			assignedCanceledTempLaborNotificationStatuses,
		} = this.props;

		if (prevProps.assignedCanceledNotificationStatuses !== assignedCanceledNotificationStatuses
			|| prevProps.assignedPublishedNotificationStatuses !== assignedPublishedNotificationStatuses
		) {
			this.setState(() => ({
				notificationStatusByEmployee: {
					...(assignedCanceledNotificationStatuses ?? {}),
					...(assignedPublishedNotificationStatuses ?? {}),
				},
			}));
		}

		if (prevProps.assignedPublishedTempLaborNotificationStatuses !== assignedPublishedTempLaborNotificationStatuses
			|| prevProps.assignedCanceledTempLaborNotificationStatuses !== assignedCanceledTempLaborNotificationStatuses
		) {
			this.setState(() => ({
				notificationStatusByTemporaryEmployee: {
					...(assignedPublishedTempLaborNotificationStatuses ?? {}),
					...(assignedCanceledTempLaborNotificationStatuses ?? {}),
				},
			}));
		}
	}

	componentWillUnmount() {
		const { isSelectMultipleEnabled, toggleWeeklyViewSelectMultiple, dueDate } = this.props;
		window.removeEventListener('resize', this.updateWindowDimensions);
		if (isSelectMultipleEnabled) {
			toggleWeeklyViewSelectMultiple(dueDate);
		}
	}

	updateWindowDimensions = () => {
		const { zoomLevel } = this.props;

		let buttonDisplayLevel = 0;

		if (window.innerWidth <= SCREEN_BREAKPOINT_M && zoomLevel > 1) {
			buttonDisplayLevel = 1;
		}

		if (window.innerWidth > SCREEN_BREAKPOINT_M && zoomLevel > 0) {
			buttonDisplayLevel = 2;
		}

		if (window.innerWidth > SCREEN_BREAKPOINT_L) {
			buttonDisplayLevel = 2;
		}

		if (buttonDisplayLevel !== this.state.buttonDisplayLevel) {
			this.setState(() => ({ buttonDisplayLevel }));
		}
	};

	openCopyDayModal = () => {
		const { workDays, isSelectMultipleEnabled, dueDate, reinitializeCopyForm, selectedWorkOrders, workOrders } = this.props;

		const _workOrders: BaseWorkOrderModel[] = isSelectMultipleEnabled ? selectedWorkOrders : Object.values(workOrders || {});

		const validityCondition = (wo: BaseWorkOrderModel) => {
			if (workOrders?.[wo.code]?.jobStatus === WorkRequestStatus.FINISHED) {
				return 'finishedOrders';
			} else if (workOrders?.[wo.code]?.status === WorkOrderStatus.CANCELED) {
				return 'cancelledOrders';
			} else {
				return 'validOrders';
			}
		};

		const { validOrders, finishedOrders, cancelledOrders } = WorkOrderUtils.groupWorkOrdersByValidity(_workOrders, validityCondition);

		const dueDateMoment = TimeUtils.parseMoment(dueDate);
		if (!dueDateMoment) {
			throw new Error('Due Date incorrectly formatted');
		}

		reinitializeCopyForm(
			dueDateMoment.add(1, 'days').format(TimeFormat.DATE_ONLY),
			dueDate,
			convertWorkDays(workDays),
			validOrders,
			finishedOrders,
			cancelledOrders
		);
		this.setState(() => ({ copyDayModalVisible: true }));
	};

	closeCopyDayModal = () => {
		this.setState(() => ({ copyDayModalVisible: false }));
	};

	copyOrdersOnDay = async (copyForm: WorkOrderCopyRM) => {
		const {
			toggleDayWeeklyView,
			copyWorkOrder,
			isSelectMultipleEnabled,
			selectedWorkOrders,
			dueDate,
		} = this.props;

		if (isSelectMultipleEnabled) {
			const expandedCopyForm: WorkOrderCopyRM = {
				...copyForm,
				selectedOrdersIds: filterMap(selectedWorkOrders,
					(_workOrder) => _workOrder.status !== WorkOrderStatus.CANCELED && _workOrder.jobStatus !== WorkRequestStatus.FINISHED,
					({ id }) => id),
			};
			await copyWorkOrder(expandedCopyForm);
			toggleDayWeeklyView(dueDate, false);
		} else {
			await copyWorkOrder(copyForm);
		}
		this.closeCopyDayModal();
	};

	showToolbarForDay = () => {
		const { dueDate, toggleWeeklyViewSelectMultiple, setWeeklyViewDateWithToolbar, isSelectMultipleEnabled } = this.props;
		if (isSelectMultipleEnabled) {
			toggleWeeklyViewSelectMultiple(dueDate);
		}
		setWeeklyViewDateWithToolbar(dueDate);
	};

	selectMultiple = () => {
		const { toggleWeeklyViewSelectMultiple, dueDate } = this.props;
		toggleWeeklyViewSelectMultiple(dueDate);
	};

	onCheck = () => {
		const { isAllSelected, toggleDayWeeklyView, dueDate } = this.props;
		toggleDayWeeklyView(dueDate, !isAllSelected);
	};

	openNotifyAllModal = async () => {
		const { findAllWorkOrdersWithNotificationsByDueDate, dueDate, selectedWorkOrders } = this.props;

		const workOrderIds = selectedWorkOrders.map((_wo) => _wo.id);

		this.setState(() => ({ notifyAllModalVisible: true }));
		const notificationLengths = await findAllWorkOrdersWithNotificationsByDueDate(dueDate, workOrderIds);
		this.setState(() => ({ workOrderNotifications: notificationLengths }));
	};

	closeNotifyAllModal = () => {
		this.setState(() => ({ notifyAllModalVisible: false }));
	};

	openExpectedNotificationResultModal = () => {
		this.setState(() => ({ showExpectedNotificationResultModal: true }));
	};

	closeExpectedNotificationResultModal = () => {
		this.setState(() => ({ showExpectedNotificationResultModal: false, assignmentsThatMightNotBeNotified: null }));
	};

	getPublishedWorkOrderList = () => {
		const { participantsEmailAndSmsStatuses, selectedWorkOrders } = this.props;
		return selectedWorkOrders.reduce<(EmailAndSmsStatusViewModel & ScheduleBoardWorkOrderViewModel)[]>((_acc, _wo) => {
			if (_wo.status === WorkOrderStatus.PUBLISHED) {
				_acc.push({ ..._wo, ...participantsEmailAndSmsStatuses[_wo.code] });
			}
			return _acc;
		}, []);
	};

	getLongestNotificationMessage = () => {
		const notificationsLengths = this.state.workOrderNotifications.map((_wo) => _wo?.notificationTemplateLength ?? 0) ?? [];
		return Math.max(...notificationsLengths);
	};

	getCanceledWorkOrderList = () => {
		const { participantsEmailAndSmsStatuses, selectedWorkOrders } = this.props;
		return selectedWorkOrders.reduce<(ScheduleBoardWorkOrderViewModel & ScheduleBoardWorkOrderViewModel)[]>((_acc, _wo) => {
			if (_wo.status === WorkOrderStatus.CANCELED) {
				_acc.push({ ..._wo, ...participantsEmailAndSmsStatuses[_wo.code] });
			}
			return _acc;
		}, []);
	};

	getUnpublishedWorkOrderList = () => {
		const { selectedWorkOrders } = this.props;
		return selectedWorkOrders.filter((_wo) => _wo.status === WorkOrderStatus.DRAFT || _wo.status === WorkOrderStatus.OUTDATED);
	};

	notifyParticipants = async (
		workOrderEmployeeIdsForSms: number[],
		workOrderEmployeeIdsForEmail: number[],
		workOrderTemporaryEmployeeIdsForEmail: number[],
		workOrderTemporaryEmployeeIdsForSms: number[],
		assignments: { employeeAssignments: EmployeeAssignments[]; temporaryEmployeeAssignments: TemporaryEmployeeAssignments[]; },
		message?: string,
		isEdited?: boolean
	) => {
		let customTipId: Nullable<number> = null;
		if (message) {
			customTipId = isEdited ? await this.createCustomDailyTip(message) : this.props.dailyTip.id;
		}
		const { sendTemplateNotification } = this.props;

		await this.showExpectedNotifyResult(assignments);

		await sendTemplateNotification({
			workOrderEmployeeSmsIds: workOrderEmployeeIdsForSms,
			workOrderEmployeeEmailIds: workOrderEmployeeIdsForEmail,
			workOrderTempEmployeeSmsIds: workOrderTemporaryEmployeeIdsForSms,
			workOrderTempEmployeeEmailIds: workOrderTemporaryEmployeeIdsForEmail,
			dailyTipId: customTipId,
		});
	};

	scheduleAutoNotify = async (
		workOrderEmployeeIdsForSms: number[],
		workOrderEmployeeIdsForEmail: number[],
		workOrderTemporaryEmployeeIdsForEmail: number[],
		workOrderTemporaryEmployeeIdsForSms: number[],
		assignments: { employeeAssignments: EmployeeAssignments[]; temporaryEmployeeAssignments: TemporaryEmployeeAssignments[]; },
		message?: string
	) => {
		const { scheduleForAutoNotify, dueDate } = this.props;

		await this.showExpectedNotifyResult(assignments);

		const customTipId = message ? await this.createCustomDailyTip(message) : undefined;
		await scheduleForAutoNotify({
			dueDate: TimeUtils.formatDate(dueDate, TimeFormat.DB_DATE_ONLY, TimeFormat.DATE_ONLY),
			workOrderEmployeeEmailIds: workOrderEmployeeIdsForEmail,
			workOrderEmployeeSmsIds: workOrderEmployeeIdsForSms,
			tempEmployeeEmailIds: workOrderTemporaryEmployeeIdsForEmail,
			tempEmployeeSmsIds: workOrderTemporaryEmployeeIdsForSms,
			dailyTipId: customTipId,
		});
	};

	createCustomDailyTip = async (message: string) => {
		const { create, dueDate } = this.props;
		const customTip = await create(message, dueDate, true) as DailyTipViewModel;
		return customTip.id;
	};

	showExpectedNotifyResult = async (
		event: { employeeAssignments: EmployeeAssignments[]; temporaryEmployeeAssignments: TemporaryEmployeeAssignments[]; }
	) => {
		const { findExpectedResultsForNotify } = this.props;
		const expectedNotifyResults = await findExpectedResultsForNotify(event);
		if (expectedNotifyResults.employeeAssignmentsThatWontReceiveNotification.flatMap((_en) => [..._en.employeeFullNames]).length
			|| expectedNotifyResults.temporaryEmployeeAssignmentsThatWontReceiveNotification.flatMap((_ten) => [..._ten.temporaryEmployeeFullNames]).length
		) {
			this.setState(() => ({ assignmentsThatMightNotBeNotified: expectedNotifyResults }));
			this.openExpectedNotificationResultModal();
		}
	};

	render() {
		const {
			dueDate,
			companyName,
			orgAlias,
			isSelectMultipleEnabled,
			isAllSelected,
			isNoneSelected,
			laborStatistics,
			hasPermissionsToEditScheduleBoard,
			notificationsDisabled,
			autoNotifyAt,
			autoNotifyOnPublish,
			isLoading,
			isAllowedToViewProdData,
			employees,
			temporaryEmployees,
			workOrderResourceLookup,
			dailyTip: { message: dailyTipMessage = '' },
			initialCopyModalData,
			notifyTemporaryLabor,
			enableScheduledNotifications,
		} = this.props;
		const {
			copyDayModalVisible,
			buttonDisplayLevel,
			notifyAllModalVisible,
			notificationStatusByEmployee,
			notificationStatusByTemporaryEmployee,
			assignmentsThatMightNotBeNotified,
			showExpectedNotificationResultModal,
		} = this.state;

		const { totalLaborStatistics, laborStatisticsPerLocation } = laborStatistics ?? {};

		const isCopyEnabled = !isSelectMultipleEnabled || (isSelectMultipleEnabled && !isNoneSelected);
		const isNotifyEnabled = !isSelectMultipleEnabled || (isSelectMultipleEnabled && !isNoneSelected);

		return (
			<div className="schedule-board-week-day-wrapper">
				<div className="schedule-board-week-day">
					<div className="left-actions">
						<Link
							to={{
								pathname: CLIENT.COMPANY.SCHEDULE_BOARD.DAILY_VIEW(orgAlias, companyName),
								state: { selectedDate: dueDate },
							}}
						>
							<div className="schedule-board-day-name">{TimeUtils.getDayName(dueDate)}</div>
							<div className="schedule-board-date">{dueDate}</div>
						</Link>
						<Button
							label="Day Toolbar"
							onClick={this.showToolbarForDay}
							style="secondary"
						/>
					</div>
					{
						hasPermissionsToEditScheduleBoard &&
						<div className="right-actions">
							{
								isSelectMultipleEnabled &&
								<Checkbox
									extraClass="schedule-board-toolbar-checkbox"
									handleChange={this.onCheck}
									isChecked={!!isAllSelected}
								/>
							}
							<div>
								<BigButton
									className="m-0"
									displayLevel={buttonDisplayLevel}
									iconClass="icon-checkbox_multiple"
									isActive={isSelectMultipleEnabled}
									label="Select WOs"
									onClick={this.selectMultiple}
								/>
							</div>
							<div className="connected-actions">
								{!notificationsDisabled &&
									<SmallButton
										displayLevel={buttonDisplayLevel}
										iconClass="icon-notifications_active"
										isDisabled={!isNotifyEnabled}
										label="Notify"
										onClick={this.openNotifyAllModal}
									/>
								}
								<SmallButton
									displayLevel={buttonDisplayLevel}
									iconClass="icon-copy"
									isDisabled={!isCopyEnabled}
									isDouble={notificationsDisabled}
									label="Copy"
									onClick={this.openCopyDayModal}
								/>
							</div>
						</div>
					}
				</div>
				{isAllowedToViewProdData &&
					<>
						<div className="schedule-board-week-day-actions">
							<LaborStatisticsPerLocation isWeekly={true} laborStatisticsPerLocation={laborStatisticsPerLocation} />
						</div>
						<TotalLaborStatistics totalLaborStatistics={totalLaborStatistics} />
					</>
				}
				<WorkOrderCopyModal
					closeCopyModal={this.closeCopyDayModal}
					initialFormData={initialCopyModalData}
					isCopyMultipleModal={true}
					onSubmit={this.copyOrdersOnDay}
					showCopyModal={copyDayModalVisible}
				/>
				<NotifyAllParticipantsModal
					autoNotifyAt={autoNotifyAt ?? null}
					autoNotifyOnPublish={autoNotifyOnPublish}
					canceledOrders={notifyAllModalVisible ? this.getCanceledWorkOrderList() : []}
					closeModal={this.closeNotifyAllModal}
					dailyTip={dailyTipMessage}
					dueDate={dueDate}
					employeeMap={employees}
					enableScheduledNotifications={enableScheduledNotifications}
					isLoading={isLoading}
					notificationMessageLength={notifyAllModalVisible ? this.getLongestNotificationMessage() : 0}
					notificationStatusByEmployee={notificationStatusByEmployee}
					notificationStatusByTemporaryEmployee={notificationStatusByTemporaryEmployee}
					notifyParticipants={this.notifyParticipants}
					notifyTemporaryLabor={notifyTemporaryLabor}
					orders={notifyAllModalVisible ? this.getPublishedWorkOrderList() : []}
					scheduleAutoNotify={this.scheduleAutoNotify}
					showModal={notifyAllModalVisible}
					temporaryEmployeeMap={temporaryEmployees}
					unpublishedOrders={notifyAllModalVisible ? this.getUnpublishedWorkOrderList() : []}
					workOrderResourceLookup={workOrderResourceLookup}
				/>
				{assignmentsThatMightNotBeNotified &&
					<ExpectedNotifyResultModal
						closeModal={this.closeExpectedNotificationResultModal}
						expectedNotifyResult={assignmentsThatMightNotBeNotified}
						showModal={showExpectedNotificationResultModal && !notifyAllModalVisible}
					/>
				}
			</div>
		);
	}
}

function mapStateToProps(state: RootState, ownProps: OwnProps) {
	const { dueDate } = ownProps;
	const {
		scheduleBoard: { weeklyViewSelectMultiple, workOrdersByDateDictionary },
		company: { company },
		user: { userData, companyData },
	} = state;
	if (!userData || !companyData) {
		throw new Error('User not logged in');
	}

	const workOrdersOnDay = workOrdersByDateDictionary[dueDate]?.workOrders;
	const workOrderResourceLookup = workOrdersByDateDictionary[dueDate]?.workOrderResourceLookups ?? {};
	const canceledWorkOrderResourceLookup = workOrdersByDateDictionary[dueDate]?.canceledWorkOrderResourceLookups ?? {};

	const isAllowedToViewProdData = isAllowed(
		PagePermissions.COMPANY.PROD_DATA.VIEW,
		companyData.permissions,
		companyData.isCompanyAdmin,
		userData.role
	);

	let isAllSelected = true;
	let isNoneSelected = true;
	let isSelectMultipleEnabled = false;
	const selectedWorkOrders: ScheduleBoardWorkOrderViewModel[] = [];

	if (weeklyViewSelectMultiple[dueDate]) {
		isSelectMultipleEnabled = true;
		if (workOrdersOnDay) {
			Object.values(workOrdersOnDay).forEach((_wo) => {
				if (isNoneSelected && weeklyViewSelectMultiple[dueDate][_wo.id]) {
					isNoneSelected = false;
				} else if (isAllSelected && !weeklyViewSelectMultiple[dueDate][_wo.id]) {
					isAllSelected = false;
				}
				if (weeklyViewSelectMultiple[dueDate][_wo.id]) {
					selectedWorkOrders.push(_wo);
				}
			});
		} else {
			isAllSelected = false;
			isNoneSelected = false;
		}
	} else {
		if (workOrdersOnDay) {
			selectedWorkOrders.push(...Object.values(workOrdersOnDay));
		}
	}
	const workOrdersOnDate = workOrdersByDateDictionary[dueDate];
	let laborStatistics: Nullable<ScheduleBoardLaborStatistics> = {
		laborStatisticsPerLocation: undefined,
		totalLaborStatistics: undefined,
	};
	if (workOrdersOnDate?.laborStatistics) {
		laborStatistics = ScheduleBoardUtil.ignoreUndefinedLocation(workOrdersOnDate.laborStatistics);
	}

	const selectedDayOfWeek = TimeUtils.parseMoment(dueDate)?.day();
	const dayBefore = selectedDayOfWeek ? selectedDayOfWeek - 1 : 6;

	const assignedPublishedNotificationStatuses = workOrdersOnDate?.assignedPublishedNotificationStatuses ?? null;
	const assignedCanceledNotificationStatuses = workOrdersOnDate?.assignedCanceledNotificationStatuses ?? null;
	const assignedPublishedTempLaborNotificationStatuses = workOrdersOnDate?.assignedPublishedTempLaborNotificationStatuses ?? null;
	const assignedCanceledTempLaborNotificationStatuses = workOrdersOnDate?.assignedCanceledTempLaborNotificationStatuses ?? null;

	const notificationSettings = company?.notification;
	const board = state.scheduleBoard;
	const workOrders = state.workOrder?.workOrders;

	return {
		isAllSelected,
		isNoneSelected,
		isSelectMultipleEnabled,
		isAllowedToViewProdData,
		selectedWorkOrders,
		workDays: company?.workDays,
		laborStatistics,
		zoomLevel: board?.zoomLevel,
		scheduleBoardView: board?.scheduleBoardView,
		workOrders: workOrdersOnDay,
		participantsEmailAndSmsStatuses: workOrders?.participantsEmailAndSmsStatuses ?? {} as EmailAndSmsStatusViewModelMap,
		isLoading: state.http.isFetching,
		notificationsDisabled: !notificationSettings?.isEnabled,
		autoNotifyOnPublish: notificationSettings?.notifyOnPublish ?? null,
		enableScheduledNotifications: notificationSettings?.enableScheduledNotifications ?? false,
		autoNotifyAt: notificationSettings?.notifyOnDay?.[dayBefore],
		workOrderResourceLookup: { ...workOrderResourceLookup, ...canceledWorkOrderResourceLookup },
		employees: board?.employees,
		temporaryEmployees: board?.temporaryEmployees,
		assignedPublishedNotificationStatuses,
		assignedCanceledNotificationStatuses,
		assignedPublishedTempLaborNotificationStatuses,
		assignedCanceledTempLaborNotificationStatuses,
		dailyTip: board?.workOrdersByDateDictionary?.[dueDate]?.dailyTip,
		initialCopyModalData: state.workOrder.copyWorkOrderModalData,
		notifyTemporaryLabor: company?.notification.notifyTemporaryLabor ?? false,
	};
}

function mapDispatchToProps() {
	return {
		activateMultiSelectMode: ScheduleBoardActions.activateMultiSelectMode,
		exportScheduleBoard: ScheduleBoardActions.exportScheduleBoard,
		clearDailyView: ScheduleBoardActions.clearDailyView,
		clearWeeklyView: ScheduleBoardActions.clearWeeklyView,
		toggleDayWeeklyView: ScheduleBoardActions.toggleDayWeeklyView,
		toggleWeeklyViewSelectMultiple: ScheduleBoardActions.toggleWeeklyViewSelectMultiple,
		setWeeklyViewDateWithToolbar: ScheduleBoardActions.setWeeklyViewDateWithToolbar,
		reinitializeCopyForm: WorkOrderActions.reinitializeCopyForm,
		findAllWorkOrdersWithNotificationsByDueDate: WorkOrderActions.findAllWorkOrdersWithNotificationsByDueDate,
		copyWorkOrder: WorkOrderActions.copyWorkOrder,
		create: DailyTipActions.create,
		scheduleForAutoNotify: NotifyActions.scheduleForAutoNotify,
		sendTemplateNotification: NotifyActions.sendTemplateNotificationMultipleWO,
		findExpectedResultsForNotify: NotifyActions.findExpectedResultsForNotify,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

export default connector(ToolbarInfo);
